import { rem } from 'polished'
import React, { useMemo } from 'react'
import styled from 'styled-components'

import ClickableLink from 'components/Common/Clickable/ClickableLink'
import Image from 'components/Common/Image'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextButton from 'components/Luxkit/Button/TextButton'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Heading from 'components/Luxkit/Typography/Heading'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'
import { themeClassName } from 'lib/theme/themeUtils'
import { DEFAULT_TRIP_IMAGE } from 'tripPlanner/config'
import CreatorProfile from 'tripPlanner/Tripkit/CuratedTrips/CreatorProfile'
import { BasicTrip } from 'tripPlanner/types/common'
import { getTripDescriptors } from 'tripPlanner/utils/curated/curatedTrips'
import { getCuratedTripURL } from 'tripPlanner/utils'

const Tile = styled(ClickableLink)`
  width: 100%;
  background-color: ${props => props.theme.palette.neutral.default.eight};
  border-radius: ${props => props.theme.borderRadius.S};
  overflow: hidden;

  display: flex;
  flex-direction: row;
  align-items: stretch;
`

const TileContent = styled(Group)`
  flex: 1;
  padding: ${rem(12)};

  > * {
    text-overflow: ellipsis;
  }
`

const TopRow = styled(Group)`
  display: none;

  ${mediaQueryUp.tablet} {
    display: initial;
  }
`

const TripImage = styled.div`
  width: ${rem(84)};
  padding: ${rem(12)} 0 ${rem(12)} ${rem(12)};

  picture {
    height: auto;
    border-radius: ${props => props.theme.borderRadius.S};
  }

  ${mediaQueryUp.tablet} {
    width: ${rem(136)};
    padding: 0;

    picture {
      height: 100%;
      border-radius: 0;
    }
  }
`

const DetailsCaption = styled(BodyText)`
  > * + *::before {
    content: '·';
    margin: 0 ${rem(4)};
    speak-as: bullets;
    speak: none;
  }
`

const ViewTripButton = styled.div`
  align-self: flex-end;
  display: none;

  ${mediaQueryUp.tablet} {
    display: initial;
  }
`

const TileBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0;
  gap: ${rem(4)};
`

interface Props {
  trip: BasicTrip
}

function TripTile(props: Props) {
  const { trip } = props

  const { metadata } = trip
  const image = useMemo((): App.Image => {
    return {
      id: trip.imageId,
      url: trip.imageId ? undefined : DEFAULT_TRIP_IMAGE,
      title: trip.name,
    }
  }, [trip])

  // const rating = 4.5

  return (
    <Tile
      to={getCuratedTripURL(trip.id)}
      data-testid={`trip-panel-${trip.id}`}
      target="_blank"
      className={themeClassName('default')}
    >
      <TripImage>
        <Image
          image={image}
          aspectRatio="1:1"
          width={136}
          fit="center"
          dpr={2}
        />
      </TripImage>
      <TileContent direction="vertical" gap={4}>
        <TopRow direction="horizontal" horizontalAlign="space-between" gap={20}>
          <BodyText variant="small" colour="neutral-three">
            Curated Trip
          </BodyText>
          {/* User count will go here */}
        </TopRow>

        <Heading variant="heading5" colour="neutral-one">
          {trip.name}
        </Heading>
        {metadata && (
          <DetailsCaption variant="medium" colour="neutral-one">
            {getTripDescriptors(metadata).map((descriptor, i) => (
              <span key={i}>{descriptor}</span>
            ))}
          </DetailsCaption>
        )}
        <Group
          direction="horizontal"
          horizontalAlign="space-between"
          verticalAlign="stretch"
          gap={20}
        >
          <VerticalSpacer gap={4}>
            <TileBody>
              {trip.curator?.showBio && trip.curator?.name && (
                <CreatorProfile
                  size="small"
                  name={trip.curator.name.replace(' ', '\xa0')}
                  imgId={trip.curator.imageId}
                />
              )}
            </TileBody>
          </VerticalSpacer>

          <ViewTripButton>
            <TextButton
              kind="secondary"
              variant="default"
              size="medium"
              shape="square"
              nonInteractable
            >
              View trip
            </TextButton>
          </ViewTripButton>
        </Group>
      </TileContent>
    </Tile>
  )
}

export default TripTile
